import { useEffect, useState } from "react";
import { PlayCircle, PauseCircle, Check, Music, X } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { playAlarmSoundOption, getPlayingAlarmSoundId, subscribeAlarmSound, playCustomAlarmSound, pickCustomAlarmSound, customSoundId } from "../../utils/alarmSound";

// One row in an alarm-sound list: label, a "selected" check, and a
// play/pause preview button. Shared by ReminderPicker (tasks/events) and
// the habit reminder form so both reflect the single shared preview
// player's state — clicking a playing row's button pauses it instead of
// restarting, and only one row across the whole app shows "playing" at once.
export function AlarmSoundRow({ option, selected, onSelect }) {
  const [playingId, setPlayingId] = useState(() => getPlayingAlarmSoundId());
  useEffect(() => subscribeAlarmSound(setPlayingId), []);
  const isPlaying = playingId === option.id;

  return (
    <div onClick={onSelect}
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", marginBottom: 5, borderRadius: 10, cursor: "pointer", background: "var(--c-bg-card)", border: selected ? "1.5px solid #d4789a" : "1.5px solid var(--c-border-light)" }}>
      <span style={{ flex: 1, fontSize: 12.5, fontWeight: selected ? 700 : 500, color: selected ? "var(--c-text-accent)" : "var(--c-text)" }}>{option.label}</span>
      {selected && <Check size={14} color="var(--c-primary)" strokeWidth={3} />}
      <button type="button" onClick={(e) => { e.stopPropagation(); playAlarmSoundOption(option); }} title={isPlaying ? `Pause ${option.label}` : `Preview ${option.label}`}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: "50%", border: "none", background: "transparent", color: "var(--c-text-accent)", cursor: "pointer" }}>
        {isPlaying ? <PauseCircle size={18} /> : <PlayCircle size={18} />}
      </button>
    </div>
  );
}

// "Choose a sound from your device" row — native only (Android's own sound
// picker, same one the Clock app uses). When a custom sound is picked it
// becomes the selection (mutually exclusive with the built-in list above);
// tapping the row again re-opens the picker to change it, and the ✕ clears
// it back to a built-in sound.
export function CustomSoundRow({ uri, label, selected, onPick, onClear }) {
  const [playingId, setPlayingId] = useState(() => getPlayingAlarmSoundId());
  useEffect(() => subscribeAlarmSound(setPlayingId), []);
  if (!Capacitor.isNativePlatform()) return null;
  const isPlaying = uri && playingId === customSoundId(uri);

  const openPicker = async () => {
    const picked = await pickCustomAlarmSound(uri);
    if (picked) onPick(picked);
  };

  return (
    <div onClick={openPicker}
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", marginBottom: 5, borderRadius: 10, cursor: "pointer", background: "var(--c-bg-card)", border: selected ? "1.5px solid #d4789a" : "1.5px dashed var(--c-border-light)" }}>
      <Music size={14} color={selected ? "var(--c-primary)" : "var(--c-text-soft)"} />
      <span style={{ flex: 1, fontSize: 12.5, fontWeight: selected ? 700 : 500, color: selected ? "var(--c-text-accent)" : "var(--c-text-mid)" }}>
        {selected ? label : "Choose from device…"}
      </span>
      {selected && <Check size={14} color="var(--c-primary)" strokeWidth={3} />}
      {selected && (
        <button type="button" onClick={(e) => { e.stopPropagation(); playCustomAlarmSound(uri); }} title={isPlaying ? `Pause ${label}` : `Preview ${label}`}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: "50%", border: "none", background: "transparent", color: "var(--c-text-accent)", cursor: "pointer" }}>
          {isPlaying ? <PauseCircle size={18} /> : <PlayCircle size={18} />}
        </button>
      )}
      {selected && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onClear(); }} title="Remove custom sound"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: "50%", border: "none", background: "transparent", color: "var(--c-text-soft)", cursor: "pointer" }}>
          <X size={14} />
        </button>
      )}
    </div>
  );
}
